import { __assign } from 'tslib';
import * as React from 'react';
import { classNames, MakeEgdsSizingLength } from 'uitk-react-utils';
import DaySelection from './uitk-day-selection.esm.js';
import styles from '../uitk-calendar.css';

var getClassNameList = classNames.bind(styles);
var preventFocusOnEmptyClick = function (e) {
    e.preventDefault();
    e.stopPropagation();
};
var EmptyDay = function (_a) {
    var className = _a.className;
    return (React.createElement("td", { onMouseDown: preventFocusOnEmptyClick, "aria-hidden": true, className: className, tabIndex: -1 }));
};
var UitkDay = React.memo(function (props) {
    var ariaLabel = props.ariaLabel, customAriaLabels = props.customAriaLabels, day = props.day, dayData = props.dayData, DayTemplateComponent = props.dayTemplateComponent, enablePastDates = props.enablePastDates, handleArrowKeyPress = props.handleArrowKeyPress, isFocusable = props.isFocusable, isFocused = props.isFocused, l10n = props.l10n, minHeight = props.minHeight, onDayClick = props.onDayClick, onDayHover = props.onDayHover, onDisabledDayClick = props.onDisabledDayClick, preventScrollOnDayFocus = props.preventScrollOnDayFocus, selectable = props.selectable, selectionType = props.selectionType, _a = props.stretchy, stretchy = _a === void 0 ? false : _a;
    var dayCellRef = React.useRef(null);
    React.useEffect(function () {
        var _a;
        if (isFocused) {
            (_a = dayCellRef.current) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: preventScrollOnDayFocus === null || preventScrollOnDayFocus === void 0 ? void 0 : preventScrollOnDayFocus.current });
            if (preventScrollOnDayFocus === null || preventScrollOnDayFocus === void 0 ? void 0 : preventScrollOnDayFocus.current) {
                preventScrollOnDayFocus.current = false;
            }
        }
    }, [isFocused, dayCellRef, preventScrollOnDayFocus]);
    var canClick = React.useMemo(function () { return DayTemplateComponent.canClick(dayData, day, enablePastDates); }, [day, dayData, DayTemplateComponent, enablePastDates]);
    var canSpan = React.useMemo(function () { return DayTemplateComponent.canSpan(dayData, day, enablePastDates); }, [day, dayData, DayTemplateComponent, enablePastDates]);
    var handleDayClick = React.useCallback(function (event) {
        if (event) {
            event.stopPropagation();
        }
        if (canClick) {
            onDayClick(day);
        }
        else if (onDisabledDayClick) {
            onDisabledDayClick(day.dateString);
        }
    }, [canClick, day, onDayClick, onDisabledDayClick]);
    var handleDayKeyboardEvents = React.useCallback(function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
            handleDayClick(e);
            // Prevent scroll on space press
            e.preventDefault();
        }
        else {
            handleArrowKeyPress(e, day);
        }
    }, [day, handleArrowKeyPress, handleDayClick]);
    var handleDayHover = React.useCallback(function () {
        if (canSpan) {
            onDayHover(day);
        }
    }, [canSpan, day, onDayHover]);
    var _b = day || {}, isPast = _b.isPast, isToday = _b.isToday, isPastMaxDate = _b.isPastMaxDate;
    var dayClasses = React.useMemo(function () {
        return getClassNameList('uitk-day', {
            'uitk-day-past': isPast,
            'uitk-day-today': isToday,
            'uitk-day-past-max-date': isPastMaxDate,
            'uitk-day-stretchy': stretchy,
            'uitk-day-has-min-height': minHeight
        });
    }, [isPast, isToday, isPastMaxDate]);
    var dayButtonClass = React.useMemo(function () {
        return getClassNameList('uitk-day-button', {
            'uitk-day-selectable': selectable && canSpan,
            'uitk-day-clickable': selectable && canClick,
            'uitk-day-selection-same-day': selectionType === 'StartAndEndSameDayEdge'
        });
    }, [canClick, canSpan, selectable, selectionType]);
    var isDayEmpty = !day;
    var dateAriaLabel = React.useMemo(function () { return (isDayEmpty ? '' : ariaLabel || DayTemplateComponent.getAriaLabel(dayData, day, l10n, enablePastDates)); }, [DayTemplateComponent, ariaLabel, day, dayData, isDayEmpty, l10n, enablePastDates]);
    var selectedStateLabel;
    if (selectionType === 'StartRangeEdge' || selectionType === 'UnconfirmedStartAndEndSameDayEdge')
        selectedStateLabel = (customAriaLabels === null || customAriaLabels === void 0 ? void 0 : customAriaLabels.startDateSelected) || l10n.formatText('calendar.selected.startDate');
    else if (selectionType === 'EndRangeEdge')
        selectedStateLabel = (customAriaLabels === null || customAriaLabels === void 0 ? void 0 : customAriaLabels.endDateSelected) || l10n.formatText('calendar.selected.endDate');
    else if (selectionType === 'SingleDate')
        selectedStateLabel = l10n.formatText('calendar.selected.singleDate');
    else if (selectionType === 'StartAndEndSameDayEdge')
        selectedStateLabel =
            (customAriaLabels === null || customAriaLabels === void 0 ? void 0 : customAriaLabels.startAndEndDateSelected) || l10n.formatText('calendar.selected.startAndEndDate');
    var dayAriaLabel = selectedStateLabel ? [dateAriaLabel, selectedStateLabel].join(', ') : dateAriaLabel;
    if (isDayEmpty) {
        var emptyDayProps = {
            className: getClassNameList({
                'uitk-day': true,
                'uitk-day-stretchy': stretchy,
                'uitk-day-has-min-height': minHeight
            })
        };
        return React.createElement(EmptyDay, __assign({}, emptyDayProps));
    }
    var tabIndex = selectionType === 'StartRangeEdge' || selectionType === 'SingleDate' || isFocusable ? 0 : -1;
    var cssCustomProps = __assign({}, (minHeight && {
        '--uitk-day-min-cell-height': "".concat(MakeEgdsSizingLength(minHeight))
    }));
    return (React.createElement("td", { className: dayClasses, style: cssCustomProps },
        React.createElement("div", { className: dayButtonClass, role: "button", ref: dayCellRef, tabIndex: tabIndex, onKeyDown: handleDayKeyboardEvents, onClick: handleDayClick, onMouseEnter: handleDayHover, "aria-disabled": !canClick },
            React.createElement("div", { className: "uitk-day-aria-label", "aria-label": dayAriaLabel }),
            React.createElement(DaySelection, { selectionType: selectionType },
                React.createElement(DayTemplateComponent, { day: day, dayData: dayData, enablePastDates: enablePastDates })))));
});
UitkDay.displayName = 'UitkDay';

export { UitkDay };
